import React, { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AGE_GROUPS } from "@/data/site";
import { WaiverForm } from "@/components/WaiverForm";

const inputCls =
  "w-full rounded-none border border-white/15 bg-zinc-900 px-4 py-3 font-body text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-neon-green focus:ring-1 focus:ring-neon-green";
const labelCls = "block font-body text-xs font-bold uppercase tracking-[0.2em] text-neon-green/80 mb-2";

export function RegistrationForm({ defaultGroup = "" }) {
  const [form, setForm] = useState({
    player_name: "",
    player_age: "",
    age_group: defaultGroup,
    guardian_name: "",
    email: "",
    phone: "",
    experience: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [regId, setRegId] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.age_group) {
      setError("Please choose an age group.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/registrations", {
        ...form,
        player_age: parseInt(form.player_age, 10) || 0,
      });
      setRegId(data.id);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (regId) {
    return (
      <div data-testid="registration-waiver-step">
        <div data-testid="registration-confirmed" className="mb-6 border border-neon-green/50 bg-zinc-900 p-6 text-center neon-glow">
          <CheckCircle2 size={44} strokeWidth={2.5} className="mx-auto text-neon-green" />
          <h3 className="mt-3 font-heading text-3xl uppercase text-white">Registration Confirmed!</h3>
          <p className="mt-2 font-body text-sm text-white/70">
            <span className="text-neon-green">{form.player_name || "Your athlete"}</span> is registered for the{" "}
            <span className="text-neon-green">{form.age_group}</span> program. Complete the quick waiver below and we'll email your full confirmation to{" "}
            <span className="text-white">{form.email}</span>.
          </p>
        </div>
        <div className="mb-6 border border-white/15 bg-zinc-900/60 px-5 py-4">
          <p className="font-body text-sm text-white/80">
            <span className="font-bold text-neon-green">One last step (Step 2 of 2):</span> please review and sign the required liability waiver below to complete sign-up.
          </p>
        </div>
        <WaiverForm regId={regId} prefill={form} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="registration-form" className="space-y-6">
      {error && (
        <div data-testid="registration-error" className="border border-red-500/50 bg-red-500/10 px-4 py-3 font-body text-sm text-red-300">
          {error}
        </div>
      )}

      <div>
        <label className={labelCls}>Choose Age Group *</label>
        <div className="grid grid-cols-3 gap-3">
          {AGE_GROUPS.map((g) => (
            <button
              key={g.id}
              type="button"
              data-testid={`group-select-${g.id}`}
              onClick={() => setForm((f) => ({ ...f, age_group: g.id }))}
              className={`border py-4 font-heading text-lg uppercase tracking-wide transition-colors ${
                form.age_group === g.id
                  ? "border-neon-green bg-neon-green text-black"
                  : "border-white/15 bg-zinc-900 text-white/70 hover:border-neon-green/60"
              }`}
            >
              {g.id}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Player Name *</label>
          <input required data-testid="input-player-name" className={inputCls} value={form.player_name} onChange={set("player_name")} placeholder="Player full name" />
        </div>
        <div>
          <label className={labelCls}>Player Age *</label>
          <input required type="number" min="5" max="18" data-testid="input-player-age" className={inputCls} value={form.player_age} onChange={set("player_age")} placeholder="e.g. 11" />
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Parent / Guardian Name *</label>
          <input required data-testid="input-guardian-name" className={inputCls} value={form.guardian_name} onChange={set("guardian_name")} placeholder="Your full name" />
        </div>
        <div>
          <label className={labelCls}>Phone *</label>
          <input required data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" />
        </div>
      </div>

      <div>
        <label className={labelCls}>Email *</label>
        <input required type="email" data-testid="input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
      </div>

      <div>
        <label className={labelCls}>Playing Experience</label>
        <input data-testid="input-experience" className={inputCls} value={form.experience} onChange={set("experience")} placeholder="Current club, recreational, first time, etc." />
      </div>

      <div>
        <label className={labelCls}>Anything Else We Should Know?</label>
        <textarea rows={3} data-testid="input-notes" className={inputCls} value={form.notes} onChange={set("notes")} placeholder="Goals, availability, questions..." />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="submit-registration-btn"
        className="group relative flex w-full items-center justify-center overflow-hidden bg-neon-green py-4 font-heading text-lg uppercase tracking-widest text-black transition-opacity disabled:opacity-60"
      >
        {loading ? <Loader2 className="animate-spin" size={22} /> : "Continue to Waiver"}
      </button>
      <p className="text-center font-body text-xs text-white/40">
        Step 1 of 2 — after this you'll review and sign the liability waiver.
      </p>
    </form>
  );
}
